import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import styles from './VerifyPage.module.css';

type Status = 'verifying' | 'success' | 'error';

const VerifyPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('verifying');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setErrorMessage('No verification token provided.');
      return;
    }

    window.history.replaceState(null, '', '/members/verify');

    const verify = async () => {
      try {
        const res = await fetch(
          `/.netlify/functions/verify-magic-link?token=${encodeURIComponent(token)}`,
          { credentials: 'include' }
        );

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Verification failed');
        }

        setStatus('success');
        setTimeout(() => {
          navigate('/members/dashboard', { replace: true });
        }, 1500);
      } catch (err) {
        setStatus('error');
        setErrorMessage(
          err instanceof Error ? err.message : 'Verification failed'
        );
      }
    };

    verify();
  }, [searchParams, navigate]);

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {status === 'verifying' && (
          <>
            <div className={styles.spinner} />
            <p className={styles.text}>Validating your access...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className={styles.successIcon}>✓</div>
            <h1 className={styles.title}>Access granted</h1>
          </>
        )}

        {status === 'error' && (
          <>
            <div className={styles.errorIcon}>✕</div>
            <h1 className={styles.title}>Access denied</h1>
            {errorMessage && (
              <p className={styles.errorText}>{errorMessage}</p>
            )}
            <Link to="/members" className={styles.retryLink}>
              Request a new link
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyPage;
